import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StressAnalytics } from '../types';
import { endpointLabel, formatClock, formatSeconds } from './format';

const COLOR_SUCCESS = '#70B77E';
const COLOR_ERROR = '#F0544F';
const COLOR_IN_FLIGHT = '#94A3B8';
const COLOR_EXEC = '#6366F1';
const COLOR_QUEUE = '#F59E0B';

const CHART_HEIGHT = 240;

function Panel({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div
      className="flex items-center justify-center text-sm text-slate-400"
      style={{ height: CHART_HEIGHT }}
    >
      {message}
    </div>
  );
}

/**
 * Calls submitted per bucket, split by how they ended. Under a healthy system
 * this tracks the burst schedule; a plateau below the configured rate means
 * something upstream is throttling.
 */
export function ThroughputChart({ analytics }: { analytics: StressAnalytics }) {
  const data = analytics.series.map((point) => ({
    ts: formatClock(point.ts),
    success: point.success,
    error: point.error,
    inFlight: Math.max(0, point.submitted - point.success - point.error),
  }));

  return (
    <Panel
      title="Throughput"
      hint={`Calls per ${analytics.bucket_seconds}s bucket, by outcome.`}
    >
      {data.length === 0 ? (
        <EmptyChart message="No calls recorded yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="ts" tick={{ fontSize: 11, fill: '#94A3B8' }} />
            <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="success" name="Success" stackId="a" fill={COLOR_SUCCESS} />
            <Bar dataKey="error" name="Error" stackId="a" fill={COLOR_ERROR} />
            <Bar dataKey="inFlight" name="In flight" stackId="a" fill={COLOR_IN_FLIGHT} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/**
 * The chart that actually answers "did load hurt?". Execution time is roughly
 * fixed in dummy mode, so a queue line climbing away from it is the service
 * running out of concurrency rather than the provider getting slower.
 */
export function LatencyOverTimeChart({ analytics }: { analytics: StressAnalytics }) {
  const data = analytics.series.map((point) => ({
    ts: formatClock(point.ts),
    execP50: point.exec_p50,
    execP90: point.exec_p90,
    queueP50: point.queue_p50,
    queueP90: point.queue_p90,
  }));
  const hasAny = data.some((d) => d.execP50 != null || d.queueP50 != null);

  return (
    <Panel
      title="Latency over time"
      hint="Execution vs queue wait, in seconds. Queue climbing while execution stays flat means the service is saturated."
    >
      {!hasAny ? (
        <EmptyChart message="No finished calls yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="ts" tick={{ fontSize: 11, fill: '#94A3B8' }} />
            <YAxis
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              tickFormatter={(v: number) => `${v.toFixed(1)}s`}
            />
            <Tooltip formatter={(v) => formatSeconds(typeof v === 'number' ? v : null)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="execP50"
              name="Exec p50"
              stroke={COLOR_EXEC}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="execP90"
              name="Exec p90"
              stroke={COLOR_EXEC}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="queueP50"
              name="Queue p50"
              stroke={COLOR_QUEUE}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="queueP90"
              name="Queue p90"
              stroke={COLOR_QUEUE}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/** Percentile bars for the three timings the backend reports separately. */
export function PercentileChart({ analytics }: { analytics: StressAnalytics }) {
  const t = analytics.totals;
  const data = [
    { label: 'p50', exec: t.exec_p50, queue: t.queue_p50, total: t.total_p50 },
    { label: 'p90', exec: t.exec_p90, queue: t.queue_p90, total: t.total_p90 },
    { label: 'p95', exec: t.exec_p95, queue: t.queue_p95, total: t.total_p95 },
    { label: 'p99', exec: t.exec_p99, queue: t.queue_p99, total: t.total_p99 },
  ];
  const hasAny = data.some((d) => d.exec != null || d.queue != null || d.total != null);

  return (
    <Panel
      title="Latency percentiles"
      hint="End-to-end is queue wait plus execution, so the tail shows where time actually went."
    >
      {!hasAny ? (
        <EmptyChart message="No finished calls yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} />
            <YAxis
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              tickFormatter={(v: number) => `${v.toFixed(1)}s`}
            />
            <Tooltip formatter={(v) => formatSeconds(typeof v === 'number' ? v : null)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="queue" name="Queue wait" fill={COLOR_QUEUE} />
            <Bar dataKey="exec" name="Execution" fill={COLOR_EXEC} />
            <Bar dataKey="total" name="End to end" fill="#0F766E" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

export function OutcomeChart({ analytics }: { analytics: StressAnalytics }) {
  const t = analytics.totals;
  const data = [
    { name: 'Success', value: t.success, fill: COLOR_SUCCESS },
    { name: 'Error', value: t.errors, fill: COLOR_ERROR },
    { name: 'In flight', value: t.in_flight, fill: COLOR_IN_FLIGHT },
  ].filter((slice) => slice.value > 0);

  return (
    <Panel title="Outcomes" hint={`${t.total} recorded call${t.total === 1 ? '' : 's'}.`}>
      {data.length === 0 ? (
        <EmptyChart message="No calls recorded yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}>
              {data.map((slice) => (
                <Cell key={slice.name} fill={slice.fill} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/**
 * How the run was distributed across endpoints and how each one held up. Only
 * worth showing once more than one endpoint was hit — i.e. for randomized runs.
 */
export function ModelBreakdownTable({ analytics }: { analytics: StressAnalytics }) {
  const groups = analytics.by_model;
  if (groups.length < 2) return null;

  return (
    <Panel
      title="By endpoint"
      hint="Which endpoints the randomized run hit, and how each one performed."
    >
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Endpoint</th>
            <th className="text-right font-medium pb-2">Calls</th>
            <th className="text-right font-medium pb-2">Errors</th>
            <th className="text-right font-medium pb-2">Exec p50</th>
            <th className="text-right font-medium pb-2">Exec p90</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.map((group) => (
            <tr key={group.model_type}>
              <td data-label="Endpoint" className="py-2 pr-4 text-slate-700">
                {endpointLabel(group.model_type)}
              </td>
              <td data-label="Calls" className="py-2 text-right text-slate-600 tabular-nums">
                {group.total}
              </td>
              <td
                data-label="Errors"
                className={`py-2 text-right tabular-nums ${
                  group.errors > 0 ? 'text-danger' : 'text-slate-400'
                }`}
              >
                {group.errors}
              </td>
              <td data-label="Exec p50" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(group.exec_p50)}
              </td>
              <td data-label="Exec p90" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(group.exec_p90)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

export function ErrorGroupsTable({ analytics }: { analytics: StressAnalytics }) {
  if (analytics.error_groups.length === 0) return null;
  return (
    <Panel
      title="Failures"
      hint="Grouped by message, so one repeated fault doesn't look like many."
    >
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Error</th>
            <th className="text-right font-medium pb-2 w-20">Count</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {analytics.error_groups.map((group) => (
            <tr key={group.error}>
              <td data-label="Error" className="py-2 pr-4 text-slate-700 font-mono text-xs break-all">
                {group.error}
              </td>
              <td data-label="Count" className="py-2 text-right text-slate-600 tabular-nums">
                {group.count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

export function SlowestTable({ analytics }: { analytics: StressAnalytics }) {
  if (analytics.slowest.length === 0) return null;
  return (
    <Panel title="Slowest calls" hint="Execution time only, excluding the queue wait.">
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Call</th>
            <th className="text-left font-medium pb-2">Status</th>
            <th className="text-right font-medium pb-2">Execution</th>
            <th className="text-right font-medium pb-2">End to end</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {analytics.slowest.map((call) => (
            <tr key={call.id}>
              <td data-label="Call" className="py-2 pr-4 font-mono text-xs text-slate-500">
                {call.id.slice(0, 8)}
              </td>
              <td data-label="Status" className="py-2 pr-4 text-slate-600">
                {call.status}
              </td>
              <td data-label="Execution" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(call.duration_seconds)}
              </td>
              <td data-label="End to end" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(call.total_seconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
